import React, { useState, useEffect } from 'react';

// Hooks
import { useDispatch } from 'react-redux';

// Utils
import { MESSAGES } from '~/utils/message';

// Actions
import { duplicateCampaign } from '~/redux/actions/campaign-actions';
import { failure, success } from '~/redux/actions/snackbar-actions';
import OlcButton from '~/components/General/OlcButton';

// MUI Components
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { CircularProgress, Input, InputAdornment } from '@mui/material';

// Icons
import { DialogCloseCIcon, DetailsCrossCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

/**
 * DuplicateCampaignModal Component
 * A modal for duplicating a campaign.
 *
 * @param {boolean} open - Boolean indicating whether the modal is open.
 * @param {Function} handleClose - Function to handle the closing of the modal.
 * @param {Object} selectedRecord - The selected campaign record to duplicate.
 * @param {Function} onSuccess - Callback function called after successful duplication.
 * @returns {JSX.Element} The duplicate campaign modal component.
 *
 */
const DuplicateCampaignModal = ({ open = false, handleClose, selectedRecord, onSuccess }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  // Handle the duplication of the campaign
  const handleDuplicate = async () => {
    const trimmedName = name.trim();
    const errorText = !trimmedName
      ? 'Campaign Name is required'
      : trimmedName.length > 50
        ? 'Campaign Name should be less than or equal to 50 characters'
        : '';
    if (errorText) {
      dispatch(failure(errorText));
      return;
    }
    setLoading(true);
    try {
      const response = await dispatch(
        duplicateCampaign({
          id: selectedRecord.campaignId,
          name: trimmedName,
        })
      );

      // Check if response is successful (status 200)
      if (response?.status === 200 && response?.data) {
        dispatch(success(response.data.message || 'Campaign duplicated successfully'));
        if (onSuccess) {
          onSuccess();
        }
        handleCloseDialog();
      } else {
        // Handle error response - the action returns error object when request fails
        // Axios error objects have error.response.data.message structure
        const errorMessage =
          response?.response?.data?.message ||
          response?.data?.message ||
          response?.message ||
          'Failed to duplicate campaign. Please try again.';
        dispatch(failure(errorMessage));
      }
    } catch (error) {
      // Handle unexpected errors that might be thrown
      const errorMessage =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        'An unexpected error occurred. Please try again.';
      dispatch(failure(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initialize name when modal is open with a selected record
    // Only set when modal is open to avoid glitch during closing animation
    // We don't reset name here - it's handled in handleCloseDialog
    if (open && selectedRecord?.name) {
      setName(`${selectedRecord.name} (Copy)`);
    }
  }, [open, selectedRecord]);

  // Close the modal
  const handleCloseDialog = () => {
    setName('');
    handleClose(false);
  };

  return (
    <BootstrapDialog
      onClose={handleCloseDialog}
      aria-labelledby="customized-dialog-title"
      open={open}
      className="duplicateModalWrapper"
    >
      <DialogTitle className="duplicateModalTitle" sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        <Typography variant="h4">Duplicate Campaign</Typography>
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          disabled={loading}
          sx={{
            padding: '0',
            transition: 'opacity 0.3s ease-in-out',
            '&:hover': { background: 'transparent', opacity: 0.4 },
          }}
        >
          <img src={DialogCloseCIcon} alt="close" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="duplicateModalContent">
        <Typography>Campaign Name</Typography>
        <Input
          type="text"
          className="duplicateModalInput"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={selectedRecord?.name}
          endAdornment={
            name && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setName('')}
                  disableRipple
                  sx={{ padding: '0', '&:hover': { background: 'transparent' } }}
                >
                  <img
                    style={{ width: '10px', height: '10px' }}
                    src={DetailsCrossCIcon}
                    alt="close"
                  />
                </IconButton>
              </InputAdornment>
            )
          }
        />
      </DialogContent>
      <DialogActions className="actionBtns">
        <OlcButton
          variant="secondary"
          disabled={loading}
          autoFocus
          onClick={handleCloseDialog}
        >
          Cancel
        </OlcButton>
        <OlcButton variant="primary" disabled={loading} autoFocus onClick={handleDuplicate}>
          {loading ? (
            <CircularProgress
              sx={{
                color: 'white',
                width: '25px !important',
                height: '25px !important',
              }}
            />
          ) : (
            'Save'
          )}
        </OlcButton>
      </DialogActions>
    </BootstrapDialog>
  );
};

export default DuplicateCampaignModal;
